import React, {useEffect, useState} from 'react';
import {classNames} from "primereact/utils";
import {setCompanyField, setEditType} from "../../../redux/actions/actions";
import {InputNumber} from "primereact/inputnumber";
import {InputText} from "primereact/inputtext";
import {useAppDispatch, useAppSelector} from "../../../redux/hooks";
import {Button} from "primereact/button";
import {displayAudioGroupId, getAllAudio, getAudioById, isValidAudioGroupId} from "../../../redux/api/apiAudio";
import {checkPermissions} from "../../../redux/permissions/permissionsUtils";
import {Dropdown} from "primereact/dropdown";
import AudioDialog from "../../Settings/AudioList/AudioDialog";
import {Divider} from "primereact/divider";

const CampaignDialog_Progressive_advanced_ACD = () => {
    const dispatch = useAppDispatch();
    const companiesData = useAppSelector(state => state.CompaniesListReducer.companiesData);
    const errorFields = useAppSelector(state => state.CompaniesListReducer.errorFields);
    const editType = useAppSelector(state => state.UsersReducer.editType);
    const jwtToken = useAppSelector(state => state.UsersReducer.userData.jwtToken);
    const archiveTableToggle = useAppSelector(state => state.CompaniesListReducer.archiveTableToggle);
    const rolesVision = useAppSelector(state => state.RolesReducer.rolesVision) as unknown as Record<string, string[]>;
    const dialerPermissions = checkPermissions(rolesVision, 'Audio');
    const detailsLoading = useAppSelector(state => state.AudioListReducer.detailsLoading);
    const audioDialogVisible = useAppSelector(state => state.AudioListReducer.audioDialogVisible);
    const statusDone = companiesData?.campaign?.statusId === 6;
    const audioIsBlock = companiesData?.permission?.audioIsBlock;
    const disabledInputClass = classNames('w-full', {'opacity-100': archiveTableToggle && !statusDone && editType});
    // @ts-ignore
    const allAudioGroup = useAppSelector(state => state.QueueListReducer.allAudioGroup);
    const detailsLoadingCampaign = useAppSelector(state => state.CompaniesListReducer.detailsLoading);
    const [selectedGroupId, setSelectedGroupId] = useState<number | string | null>(null);
    const [selectedSchedulerGroupId, setSelectedSchedulerGroupId] = useState<number | null>(null);
    const [field, setField] = useState<any>(null)

    const {
        visible: hasAudioVisiblePermission,
        update: hasAudioUpdatePermission,
    } = dialerPermissions;

    const sameAudioId = (a: any, b: any) =>
        a != null && b != null && String(a) === String(b);

    const findAccessibleAudio = (audioId: any) =>
        allAudioGroup?.find((audio: any) => sameAudioId(audio.audioId, audioId));

    const canOpenAudioDetails = (audioId: any) => {
        if (detailsLoadingCampaign || audioIsBlock || !hasAudioVisiblePermission || audioId == null) {
            return false;
        }
        const audio = findAccessibleAudio(audioId);
        return isValidAudioGroupId(audio?.groupId);
    };

    const audioDetailsIcon = archiveTableToggle && hasAudioUpdatePermission ? 'pi pi-pencil' : 'pi pi-eye';

    const mohAudioFromDict = findAccessibleAudio(companiesData?.campaignACD?.mohAudioId);
    const showMohAudioReadOnly = !!audioIsBlock;

    const audioOptions = (() => {
        const base = allAudioGroup || [];
        const mohAudioId = companiesData?.campaignACD?.mohAudioId;
        if (
            audioIsBlock ||
            !mohAudioId ||
            base.some((item: { audioId: any }) => sameAudioId(item.audioId, mohAudioId))
        ) {
            return base;
        }
        return [
            ...base,
            {
                audioId: mohAudioId,
                audioName: companiesData?.campaignACD?.mohAudioName || String(mohAudioId),
                groupId: displayAudioGroupId(mohAudioFromDict?.groupId, mohAudioId, companiesData?.campaignACD?.mohAudioGroupName, 'campaign'),
                groupName: companiesData?.campaignACD?.mohAudioGroupName || companiesData?.campaignACD?.mohAudioName || String(mohAudioId),
            },
        ];
    })();

    useEffect(() => {
        if (detailsLoadingCampaign) {
            return;
        }
        if (audioIsBlock) {
            return;
        }
        dispatch(getAllAudio(jwtToken?.jwtToken))
    }, [audioIsBlock, detailsLoadingCampaign, jwtToken?.jwtToken]);

    useEffect(() => {
        if (audioIsBlock || detailsLoadingCampaign) {
            return;
        }
        if (isValidAudioGroupId(mohAudioFromDict?.groupId)) {
            setSelectedGroupId(mohAudioFromDict.groupId);
            return;
        }
        const mohAudioId = companiesData?.campaignACD?.mohAudioId;
        if (!mohAudioId) {
            return;
        }
        const campaignAudio = audioOptions.find((item: { audioId: any }) => sameAudioId(item.audioId, mohAudioId));
        if (campaignAudio?.groupId != null) {
            setSelectedGroupId(campaignAudio.groupId);
        }
    }, [
        audioIsBlock,
        detailsLoadingCampaign,
        companiesData?.campaignACD?.mohAudioId,
        companiesData?.campaignACD?.mohAudioGroupName,
        allAudioGroup,
    ]);

    useEffect(() => {
        if (audioIsBlock) {
            return;
        }
        const selectedSchedulerAudio = allAudioGroup?.find((audio: {
            audioId: any;
        }) => sameAudioId(audio.audioId, companiesData?.campaignACD?.schedulerAudioId));
        if (isValidAudioGroupId(selectedSchedulerAudio?.groupId)) {
            setSelectedSchedulerGroupId(selectedSchedulerAudio.groupId);
        }
    }, [audioIsBlock, companiesData?.campaignACD?.schedulerAudioId, allAudioGroup]);

    const audioItems = (audioId: any, audioField: string) => {
        return audioOptions?.map((item: any, index: number) => {
            if (sameAudioId(audioId, item.audioId)) {
                const accessibleAudio = findAccessibleAudio(item.audioId);
                return (
                    <div key={index} className="flex align-items-center justify-content-between mt-2 p-0">
                        {canOpenAudioDetails(item.audioId) &&
                            <Button
                                className="max-w-4rem p-button-secondary mr-3"
                                style={{minWidth: '40px'}}
                                loading={detailsLoading && (audioField === field)}
                                disabled={!archiveTableToggle || detailsLoading}
                                icon={audioDetailsIcon}
                                onClick={() => {
                                    if (!accessibleAudio?.groupId) {
                                        return;
                                    }
                                    dispatch(getAudioById(accessibleAudio.groupId, jwtToken?.jwtToken, true));
                                    setField(audioField);
                                    if (hasAudioUpdatePermission) {
                                        dispatch(setEditType(true));
                                    } else {
                                        dispatch(setEditType(false));
                                    }
                                }}/>
                        }
                        <div className="field col-12 md:col-8 flex align-items-center m-0 p-0">
                            {item.audioName}
                        </div>
                        <Button name={item.audioId}
                                onClick={() => {
                                    dispatch(setCompanyField({section: 'campaignACD', fieldName: audioField, value: null}));
                                }}
                                icon="pi pi-times"
                                className="delete-user p-button-secondary p-button-rounded p-button-text mb-0 field col-12 md:col-1"
                                disabled={!archiveTableToggle || !editType || audioIsBlock}
                        />
                    </div>
                )
            }
        })
    }

    const uniqueAudioGroups = Array.from(
        new Map(
            (audioOptions || [])
                .filter((item: { groupId: any; }) => item.groupId != null)
                .map((item: { groupId: any; groupName: any }) => [
                item.groupId,
                { groupId: item.groupId, groupName: item.groupName },
            ])
        ).values()
    ) as { groupId: any; groupName: any }[];

    return (
        <div className="p-fluid grid col-12 md:col-12">
            <div className="p-fluid field col-12 md:col-12 mb-0">
                <div className={classNames('mb-1 font-bold', {'p-error': errorFields?.mohAudioId})}>Аудіо
                    очікування*
                </div>
            </div>

            <div className="p-fluid field col-12 md:col-3 mb-0">
                <div className={classNames('mb-4', {'p-error': errorFields?.mohAudioId})}>Група аудіо*</div>
                {showMohAudioReadOnly ? (
                    <InputText
                        className={disabledInputClass}
                        disabled
                        value={companiesData?.campaignACD?.mohAudioGroupName || ''}
                    />
                ) : (
                    <Dropdown
                        filter
                        value={selectedGroupId}
                        onChange={(e) => {
                            setSelectedGroupId(e.value);
                            const group = uniqueAudioGroups.find(
                                (g: { groupId: any; groupName: any }) => g.groupId === e.value
                            );
                            dispatch(setCompanyField({
                                section: 'campaignACD',
                                fieldName: 'mohAudioId',
                                value: null
                            }));
                            dispatch(setCompanyField({
                                section: 'campaignACD',
                                fieldName: 'mohAudioName',
                                value: null
                            }));
                            dispatch(setCompanyField({
                                section: 'campaignACD',
                                fieldName: 'mohAudioGroupName',
                                value: group?.groupName || null
                            }));
                        }}
                        className="w-full mr-2"
                        options={uniqueAudioGroups}
                        optionLabel="groupName"
                        optionValue="groupId"
                        disabled={!archiveTableToggle || !editType || audioIsBlock}
                        placeholder="Виберіть групу"
                    />
                )}
                {errorFields?.mohAudioTime && <div className="col-12 md:col-12 pt-2 px-0 pb-0"><span
                    className="title p-error text-sm">{errorFields?.mohAudioTime?.mohAudioTime}</span></div>}
            </div>
            <div className="flex-column field col-12 md:col-3 mb-0">
                <div>
                    <div className={classNames('mb-4', {'p-error': errorFields?.mohAudioTime})}>Тривалість
                        відтворення (мін. 10 сек)*
                    </div>
                </div>
                <InputNumber
                    min={0}
                    id="maxWaitTime"
                    value={companiesData?.campaignACD?.mohAudioTime || null}
                    useGrouping={false}
                    className={classNames('', {'p-invalid': errorFields?.mohAudioTime})}
                    disabled={!archiveTableToggle || !editType || audioIsBlock}
                    onChange={(e) => {
                        dispatch(setCompanyField({section: 'campaignACD', fieldName: 'mohAudioTime', value: e.value}));
                    }}
                    placeholder="0"
                />
                {errorFields?.mohAudioTime && <div className="col-12 md:col-12 pt-2 px-0 pb-0"><span
                    className="title p-error text-sm">{errorFields?.mohAudioTime?.mohAudioTime}</span></div>}
            </div>
            <div className="col-12 md:col-12"></div>

            <div className="p-fluid field col-12 md:col-3 mb-0">
                <div className={classNames('mb-2', {'p-error': errorFields?.mohAudioId})}>Аудіо*</div>
                {showMohAudioReadOnly ? (
                    <InputText
                        className={classNames(disabledInputClass, {'p-invalid': errorFields?.mohAudioId})}
                        disabled
                        value={companiesData?.campaignACD?.mohAudioName || ''}
                    />
                ) : (
                    <Dropdown
                        filter
                        value={companiesData?.campaignACD?.mohAudioId || null}
                        onChange={(e) => {
                            const audio = audioOptions?.find((item: any) => item.audioId === e.value);
                            dispatch(setCompanyField({
                                section: 'campaignACD',
                                fieldName: 'mohAudioId',
                                value: e.value
                            }));
                            dispatch(setCompanyField({
                                section: 'campaignACD',
                                fieldName: 'mohAudioName',
                                value: audio?.audioName || null
                            }));
                            dispatch(setCompanyField({
                                section: 'campaignACD',
                                fieldName: 'mohAudioGroupName',
                                value: audio?.groupName || companiesData?.campaignACD?.mohAudioGroupName || null
                            }));
                        }}
                        options={audioOptions?.filter((item: {
                            groupId: number | null;
                        }) => item.groupId === selectedGroupId)}
                        optionLabel="audioName"
                        optionValue="audioId"
                        placeholder="Виберіть аудіо"
                        className={classNames('w-full', {'p-invalid': errorFields?.mohAudioId})}
                        disabled={!archiveTableToggle || !editType || audioIsBlock || !selectedGroupId}
                    />
                )}

                {!showMohAudioReadOnly && <div className="col-12 md:col-12 pt-2 px-0 pb-0">
                    {
                        companiesData?.campaignACD?.mohAudioId &&
                        <div className="users-list">
                            {audioItems(companiesData?.campaignACD?.mohAudioId, 'mohAudioId')}
                        </div>
                    }
                </div>}
                {errorFields?.mohAudioId && <div className="col-12 md:col-12 pt-2 px-0 pb-0"><span
                    className="title p-error text-sm">{errorFields?.mohAudioId?.mohAudioId}</span></div>}
            </div>
            {audioDialogVisible && (
                <AudioDialog skipBranchesFetch={companiesData?.permission?.branchIsBlock}/>
            )}
        </div>
    );
};

export default CampaignDialog_Progressive_advanced_ACD;
